import { Application } from '@/types/application';
import { Metadata } from '@/types/metadata/metadata';
import { Subroutine } from '@/types/subroutine';
import { Variable, VariableRef, VariableScope } from '@/types/variable';
import { isArray, isObject, isVariable } from './typeGuards';

const getVarType = (
  variables: Variable[] | Record<number, Variable[]>,
  variableName: string,
  subroutineId?: number
): string | undefined => {
  if (!isArray(variables)) {
    if (!subroutineId || !variables) return;
    return variables[subroutineId]?.find((variable) => variable.name === variableName)?.type ?? undefined;
  }

  return variables?.find((variable) => variable.name === variableName)?.type ?? undefined;
};

export const getChipType = (
  value: VariableRef,
  application: Application,
  metadata: Metadata,
  subroutineId?: number
): string | undefined => {
  //find variable
  if (value.scope === 'local' && subroutineId) {
    // application, not metadata.
    const local = application.variables['local'];
    const subVars = local[subroutineId];
    const variableType = getVarType(subVars, value.name, subroutineId);
    return variableType;
  } else {
    // could be application or metadata
    //try metadata
    const metaScope = metadata.variables && metadata.variables[value.scope];
    const metaType = metaScope && getVarType(metaScope, value.name);

    const appScope = application.variables[value.scope];
    const appType = appScope && getVarType(appScope, value.name);
    return appType || metaType;
  }
};

export const getScopeLabel = (variableScope: VariableScope, metadata: Metadata) => {
  const currentScope = metadata.scopes?.find((metadataScope) => metadataScope.scope === variableScope);
  return currentScope?.label;
};

export const getScopeType = (listName: string, metadata: Metadata) =>
  metadata.scopelists?.find((list) => list.name === listName)?.scopes;

export const variableSearchOptions = [
  { name: 'name', label: 'Name' },
  { name: 'date', label: 'Date' }
];

export const variableCustomTooltip = [{ name: 'date', tooltip: { inverted: 'Sort New-Old', upright: 'Sort Old-New' } }];

export const getVariableTypeLabel = (
  path: VariableRef,
  application: Application,
  metadata: Metadata,
  subroutineId?: number
) => {
  const variableType = getChipType(path, application, metadata, subroutineId);

  return variableType && metadata.datatypes?.find((datatype) => datatype.type === variableType)?.label;
};

export const getVariableScope = (
  scopes: VariableScope[] | undefined,
  variableName: string,
  subroutineId: number,
  application: Application,
  metadata: Metadata
): VariableScope | undefined => {
  return scopes?.find((scope) => {
    if (scope === 'local') {
      return application.variables.local[subroutineId]?.find((localVariable) => localVariable.name === variableName);
    }

    const appScope = application.variables[scope]?.find((variable) => variable.name === variableName);
    const metaScope =
      metadata.variables && metadata.variables[scope]?.find((variable) => variable.name === variableName);

    return appScope || metaScope;
  });
};

const findVariable = (
  application: Application,
  detail: unknown,
  blockId: number,
  variable: Variable,
  scope: string
): number[] => {
  let result = [];
  if (isVariable(detail)) {
    if (detail.name === variable.name && detail.scope === scope) {
      result.push(blockId);
    }
  } else if (isObject(detail)) {
    Object.entries(detail).forEach(([_, subDetailValue]) => {
      const foundVariable = findVariable(application, subDetailValue, blockId, variable, scope);
      if (foundVariable.length) {
        result = foundVariable;
      }
    });
  } else if (isArray(detail)) {
    detail.map((subDetail) => {
      const foundVariable = findVariable(application, subDetail, blockId, variable, scope);
      if (foundVariable.length) {
        result = foundVariable;
      }
    });
  }

  return result;
};

export const variableIsInUse = (
  variable: Variable,
  scope: string,
  application: Application,
  currentSubroutine?: Subroutine
): inUse => {
  let result: inUse = undefined;
  const subroutines =
    scope === 'local'
      ? [application.subroutines.find((sub) => sub.id === currentSubroutine?.id)]
      : application.subroutines;

  subroutines.forEach((subroutine) =>
    subroutine?.nodes?.forEach((node) => {
      application.blocks.details[node] &&
        Object.entries(application.blocks.details[node]).forEach(([_, detail]) => {
          if (result === 'green') return;

          const foundVariable = findVariable(application, detail, node, variable, scope);
          if (foundVariable.length) {
            if (scope === 'local') {
              result = 'green';
            } else {
              const inCurrentSubroutine = Boolean(
                foundVariable.find((foundVar) => currentSubroutine?.nodes.includes(foundVar))
              );
              result = inCurrentSubroutine ? 'green' : 'grey';
            }
          }
        });

      if (application.blocks.event_handlers && application.blocks.event_handlers[node]) {
        Object.entries(application.blocks.event_handlers[node]).forEach(([_, handler]) => {
          handler.arguments?.forEach((argument) => {
            if (
              result !== 'green' &&
              isVariable(argument) &&
              argument.name === variable.name &&
              argument.scope === scope
            ) {
              const inCurrentSubroutine = Boolean(currentSubroutine?.nodes.includes(node));
              result = inCurrentSubroutine ? 'green' : 'grey';
            }
          });
        });
      }
    })
  );

  if (!result && scope === 'local') {
    const bindings = [...(currentSubroutine?.arg_bindings ?? []), ...(currentSubroutine?.event_arg_bindings ?? [])];
    if (bindings.includes(variable.name)) {
      result = 'green';
    }
  }

  return result;
};
